import { activeChain, robinhoodMainnet } from './chains';

// Testnet deployed via contracts/scripts/deploy.js on 2026-08-25 -- see
// contracts/deployment.json for the full deployment record (mock price feed,
// treasury, etc).
const TESTNET_MINT_ADDRESS = '0x37c1B1B9642fe62C9DC191CAc6CfDd62b29f04c9';

// Mainnet deployed via contracts/scripts/deploy-mainnet.js on 2026-08-27 --
// see contracts/deployment-mainnet.json for the full deployment record (real
// Chainlink ETH/USD feed, treasury, final IPFS baseURI baked into constructor).
const MAINNET_MINT_ADDRESS = '0xAcEE8318A23e03c05e84FB12C5E5a287D865A425';

export const LILVERSE_MINT_ADDRESS =
    activeChain.id === robinhoodMainnet.id ? MAINNET_MINT_ADDRESS : TESTNET_MINT_ADDRESS;

export const LILVERSE_MINT_ABI = [
    {
        type: 'function',
        name: 'mintPublic',
        stateMutability: 'payable',
        inputs: [
            { name: 'quantity', type: 'uint256' },
            { name: 'maxWeiPerToken', type: 'uint256' },
        ],
        outputs: [],
    },
    {
        type: 'function',
        name: 'mintOg',
        stateMutability: 'payable',
        inputs: [
            { name: 'quantity', type: 'uint256' },
            { name: 'maxWeiPerToken', type: 'uint256' },
            { name: 'proof', type: 'bytes32[]' },
        ],
        outputs: [],
    },
    {
        type: 'function',
        name: 'quotePublicPriceWei',
        stateMutability: 'view',
        inputs: [{ name: 'quantity', type: 'uint256' }],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'quoteOgPriceWei',
        stateMutability: 'view',
        inputs: [{ name: 'quantity', type: 'uint256' }],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'quoteOgTotalWei',
        stateMutability: 'view',
        inputs: [{ name: 'quantity', type: 'uint256' }],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'ogMintEnabled',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'bool' }],
    },
    {
        type: 'function',
        name: 'mintingActive',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'bool' }],
    },
    {
        type: 'function',
        name: 'publicPriceCents',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'uint256' }],
    },
    // Supply counters -- read by the homepage mint progress bar.
    {
        type: 'function',
        name: 'totalMinted',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'MAX_SUPPLY',
        stateMutability: 'view',
        inputs: [],
        outputs: [{ type: 'uint256' }],
    },
    {
        type: 'function',
        name: 'ogLeafClaimed',
        stateMutability: 'view',
        inputs: [{ type: 'bytes32' }],
        outputs: [{ type: 'bool' }],
    },
    // Custom errors -- included so viem/wagmi can decode a revert into a
    // readable message instead of a bare "execution reverted".
    { type: 'error', name: 'MintingNotActive', inputs: [] },
    { type: 'error', name: 'OgMintNotEnabled', inputs: [] },
    { type: 'error', name: 'ExceedsMaxSupply', inputs: [] },
    { type: 'error', name: 'ExceedsWalletLimit', inputs: [] },
    { type: 'error', name: 'ZeroQuantity', inputs: [] },
    { type: 'error', name: 'InsufficientPayment', inputs: [{ name: 'required', type: 'uint256' }, { name: 'sent', type: 'uint256' }] },
    { type: 'error', name: 'PriceExceedsMax', inputs: [{ name: 'required', type: 'uint256' }, { name: 'max', type: 'uint256' }] },
    { type: 'error', name: 'StalePrice', inputs: [{ name: 'updatedAt', type: 'uint256' }, { name: 'nowTs', type: 'uint256' }] },
    { type: 'error', name: 'IncompleteRound', inputs: [] },
    { type: 'error', name: 'InvalidPrice', inputs: [] },
    { type: 'error', name: 'InvalidPriceFeedDecimals', inputs: [{ name: 'got', type: 'uint8' }, { name: 'expected', type: 'uint8' }] },
    { type: 'error', name: 'SequencerDown', inputs: [] },
    { type: 'error', name: 'SequencerGracePeriodNotOver', inputs: [{ name: 'timeLeft', type: 'uint256' }] },
    { type: 'error', name: 'PriceAgeOutOfBounds', inputs: [] },
    { type: 'error', name: 'InvalidMerkleProof', inputs: [] },
    { type: 'error', name: 'LeafAlreadyClaimed', inputs: [] },
    { type: 'error', name: 'ZeroAddress', inputs: [] },
    { type: 'error', name: 'WithdrawFailed', inputs: [] },
    { type: 'error', name: 'RefundFailed', inputs: [] },
];

// The OG Merkle proof set is served as a static public asset and fetched on
// demand rather than bundled into the JS chunk, so it doesn't inflate every
// page load for users who never touch the OG claim flow. Testnet and mainnet
// have different eligible-address sets and different Merkle roots (the
// mainnet root is the clean, exhaustively-verified 1,632-address list with
// test/excluded addresses removed) -- each network must fetch its own file.
const OG_PROOFS_PATH =
    activeChain.id === robinhoodMainnet.id ? '/og-merkle-proofs-mainnet.json' : '/og-merkle-proofs.json';

let ogProofsPromise = null;
export function fetchOgProofs() {
    if (!ogProofsPromise) {
        ogProofsPromise = fetch(OG_PROOFS_PATH)
            .then((res) => {
                if (!res.ok) throw new Error(`Failed to load OG proof set (${res.status})`);
                return res.json();
            })
            .catch((err) => {
                ogProofsPromise = null; // allow retry on next call
                throw err;
            });
    }
    return ogProofsPromise;
}
